import { ErrorBoundary } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { investmentStrategyFilterDiversifierAtom } from '../../../../../../../store/home/conversion/atom';
import { investmentStrategyDiversifierSelector } from '../../../../../../../store/home/conversion/selector';
import { ConversionInvestmentStrategyDiversifierSuccessResponseNormalized } from '../../../../../../../store/home/conversion/types';
import { InvestmentStrategyListTypes } from '../../../types';
import { InvestmentStrategyItem } from '../InvestmentStrategyItem';
import { InvestmentStrategyListEmpty } from '../InvestmentStrategyListEmpty';

const styles = StyleSheet.create({
  container: {
    marginLeft: -Utils.Token.ToInt(Spacing, 'inset.xs'),
    marginRight: -Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
  containerDesktop: {
    maxHeight: 400,
    marginTop: Utils.Token.ToInt(Spacing, 'inset.xs'),
    paddingBottom: Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
  containerMobile: {
    flex: 1,
  },
  separator: {
    height: Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
});

const Separator = () => <View style={styles.separator} />;

export const InvestmentStrategyListDiversifier: React.FC<InvestmentStrategyListTypes.Props> = ({
  isMobile,
  searchTerm,
}) => {
  const { appliedFilters } = useRecoilValue(investmentStrategyFilterDiversifierAtom);
  const nonNullFilters = appliedFilters.filter((filter) => filter?.filter !== null);
  const dateFilter = nonNullFilters.find((filter) => filter?.fieldName === 'date')?.filter as
    | Date
    | undefined;
  const suitabilityFilter = nonNullFilters.find((filter) => filter?.fieldName === 'suitability')
    ?.filter as string | undefined;
  const customerNameFilter = nonNullFilters.find((filter) => filter?.fieldName === 'customerName')
    ?.filter as string | undefined;

  const [allData, setAllData] = React.useState<
    ConversionInvestmentStrategyDiversifierSuccessResponseNormalized[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const { data, loading, refresher, error } = useRecoilQuery(
    investmentStrategyDiversifierSelector({
      page,
      searchTerm,
      date: dateFilter,
      suitability: suitabilityFilter,
      customerName: customerNameFilter,
    })
  );

  const handleEndReached = () => {
    if (!data?.meta.pagination.hasNext || loading || error) {
      return;
    }

    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (!data?.data) {
      return;
    }

    if (page === 1) {
      setAllData(data.data);
    } else {
      setAllData((prev) => [...prev, ...data.data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  useEffect(() => {
    setPage(1);
    setAllData([]);
  }, [searchTerm, dateFilter, suitabilityFilter, customerNameFilter]);

  if (error) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <FlatList
      data={allData}
      style={[styles.container, isMobile ? styles.containerMobile : styles.containerDesktop]}
      onEndReachedThreshold={0.4}
      onEndReached={handleEndReached}
      ItemSeparatorComponent={Separator}
      showsVerticalScrollIndicator={false}
      renderItem={({ item }) => <InvestmentStrategyItem item={item} />}
      ListEmptyComponent={<InvestmentStrategyListEmpty isLoading={loading} />}
      ListFooterComponent={
        loading && page > 1 ? <InvestmentStrategyListEmpty isLoading={loading} /> : null
      }
    />
  );
};
